import { useState } from 'react';
import { trpc } from '../lib/trpc';
import { MediaGrid } from './MediaLibrary/MediaGrid';
import type { Media } from '@wineryxplorer/shared';

export interface UserGeneratedContentManagerProps {
  venueId: string;
}

export function UserGeneratedContentManager({ venueId }: UserGeneratedContentManagerProps) {
  const [selectedMedia, setSelectedMedia] = useState<Set<string>>(new Set());

  // Fetch user-generated content for this venue
  const { data: userContent, isPending, refetch } = trpc.media.getUserGeneratedContent.useQuery({
    entityType: 'venue',
    entityId: venueId,
    limit: 50,
  });

  // Mutations for moderating user content
  const moderateContentMutation = trpc.media.moderateUserContent.useMutation({
    onSuccess: () => {
      refetch();
      setSelectedMedia(new Set());
    },
  });

  const handleFeature = (mediaIds: string[]) => {
    mediaIds.forEach(mediaId => {
      moderateContentMutation.mutate({
        mediaId,
        action: 'feature',
      });
    });
  };

  const handleHide = (mediaIds: string[]) => {
    mediaIds.forEach(mediaId => {
      moderateContentMutation.mutate({
        mediaId,
        action: 'hide',
      });
    });
  };

  const handleSelectMedia = (media: Media) => {
    // For now, just toggle selection - could open detail modal later
    setSelectedMedia(prev => {
      const newSet = new Set(prev);
      if (newSet.has(media.id)) {
        newSet.delete(media.id);
      } else {
        newSet.add(media.id);
      }
      return newSet;
    });
  };

  const handleToggleSelectMedia = (media: Media) => {
    setSelectedMedia(prev => {
      const newSet = new Set(prev);
      if (newSet.has(media.id)) {
        newSet.delete(media.id);
      } else {
        newSet.add(media.id);
      }
      return newSet;
    });
  };

  // Cast media to proper type
  const mediaList = (userContent?.items ?? []) as Media[];
  const _selectedMediaArray = mediaList.filter((m) => selectedMedia.has(m.id));

  if (isPending) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <p className="text-gray-600">Loading user-generated content...</p>
      </div>
    );
  }

  if (!userContent || userContent.items.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="text-center py-8">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No User Photos Yet</h3>
          <p className="text-gray-600">
            When visitors upload photos of your venue, they'll appear here for you to moderate.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-medium text-gray-900">
            User Photos ({userContent.total})
          </h4>
          <p className="text-sm text-gray-600 mt-1">
            Moderate photos uploaded by visitors to your venue
          </p>
        </div>

        {selectedMedia.size > 0 && (
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-600">
              {selectedMedia.size} selected
            </span>
            <button
              onClick={() => handleFeature(Array.from(selectedMedia))}
              disabled={moderateContentMutation.isPending}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:bg-gray-400"
            >
              Feature
            </button>
            <button
              onClick={() => handleHide(Array.from(selectedMedia))}
              disabled={moderateContentMutation.isPending}
              className="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:bg-gray-400"
            >
              Hide
            </button>
          </div>
        )}
      </div>

      {/* Media Grid */}
      <MediaGrid
        media={mediaList}
        selectedMedia={selectedMedia}
        onSelectMedia={handleSelectMedia}
        onToggleSelectMedia={handleToggleSelectMedia}
        showCheckboxes={true}
        showPublicationStatus={true}
        showTags={true}
        showUploader={true}
        isLoading={isPending}
        emptyMessage="No user-generated content found"
      />

      {/* Instructions */}
      <div className="mt-6 p-4 bg-primary-50 rounded-md">
        <h5 className="text-sm font-medium text-primary-900 mb-2">Moderation Guidelines</h5>
        <ul className="text-sm text-primary-800 space-y-1">
          <li>• <strong>Feature:</strong> Make photos visible to all visitors on your venue page</li>
          <li>• <strong>Hide:</strong> Remove photos from public view while keeping them in the system</li>
          <li>• Photos are uploaded by visitors and default to being featured</li>
          <li>• Use moderation to maintain quality and appropriateness</li>
        </ul>
      </div>
    </div>
  );
}